import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Settings, Users, BadgeEuro, Building2, TrendingUp, Check, Loader2, Save } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLAN_COLORS = { standard: "#10B981", entreprise: "#2563EB", premium: "#7C3AED" };

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);

  const load = useCallback(async () => {
    const [s, u, lp] = await Promise.all([
      api.get("/admin/stats").then((r) => r.data).catch(() => null),
      api.get("/admin/users").then((r) => r.data).catch(() => []),
      api.get("/license-plans").then((r) => r.data).catch(() => ({ plans: [], modules: [] })),
    ]);
    setStats(s); setUsers(u); setPlans(lp.plans || []); setModules(lp.modules || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== "admin") return <div className="p-12 text-center text-zinc-400" data-testid="admin-denied">Accès réservé à l'administrateur FlowDesk.</div>;

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center gap-2.5 mb-1"><Settings size={22} className="text-klein" /><h1 className="font-cabinet font-black text-3xl tracking-tight">Administration FlowDesk</h1></div>
        <p className="text-zinc-400 text-sm mb-6">Gérez les utilisateurs, les licences et le suivi commercial de FlowDesk.</p>

        <div className="flex items-center gap-1 bg-card border border-white/10 rounded-full p-1 w-fit mb-6">
          {[["dashboard", "Tableau de bord"], ["users", "Utilisateurs"], ["licenses", "Licences"]].map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full text-sm font-medium ${tab === k ? "bg-klein text-zinc-950" : "text-zinc-400"}`}>{l}</button>
          ))}
        </div>

        {tab === "dashboard" && <Dashboard stats={stats} />}
        {tab === "users" && <UsersTable users={users} />}
        {tab === "licenses" && <Licenses plans={plans} modules={modules} onSaved={load} />}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-card p-5">
      <div className="flex items-center gap-2 text-zinc-400 text-xs mb-2"><Icon size={15} style={{ color }} /> {label}</div>
      <div className="font-cabinet font-black text-3xl tabular-nums">{value}</div>
    </div>
  );
}

function Dashboard({ stats }) {
  if (!stats) return <div className="py-12 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>;
  return (
    <div data-testid="admin-dashboard" className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Utilisateurs" value={stats.users} color="#3B82F6" />
        <Stat icon={Building2} label="Entreprises" value={stats.companies} color="#10B981" />
        <Stat icon={TrendingUp} label="MRR estimé" value={`${(stats.estimated_mrr || 0).toLocaleString("fr-FR")} €`} color="#7C3AED" />
        <Stat icon={BadgeEuro} label="ARR estimé" value={`${((stats.estimated_mrr || 0) * 12).toLocaleString("fr-FR")} €`} color="#F59E0B" />
      </div>
      <div className="rounded-2xl border border-white/10 bg-card p-5">
        <h3 className="font-cabinet font-bold mb-3">Répartition par licence</h3>
        <div className="space-y-2">
          {Object.entries(stats.by_plan || {}).map(([k, n]) => (
            <div key={k} className="flex items-center gap-3" data-testid={`admin-byplan-${k}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: PLAN_COLORS[k] || "#71717a" }} />
              <span className="text-sm capitalize w-28">{k}</span>
              <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(n / Math.max(1, stats.companies)) * 100}%`, background: PLAN_COLORS[k] || "#71717a" }} /></div>
              <span className="text-sm tabular-nums w-8 text-right">{n}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsersTable({ users }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="admin-users">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-zinc-400 text-xs border-b border-white/10"><th className="px-4 py-3">Nom</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Rôle</th><th className="px-4 py-3">Entreprises / licences</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-b border-white/[0.06]">
              <td className="px-4 py-3 font-medium">{u.name}</td>
              <td className="px-4 py-3 text-zinc-400">{u.email}</td>
              <td className="px-4 py-3">{u.role === "admin" ? <span className="px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400 text-[11px] font-semibold">ADMIN</span> : <span className="text-zinc-400">{u.role}</span>}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                  {u.companies.length === 0 && <span className="text-zinc-600 text-xs">—</span>}
                  {u.companies.map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background border border-white/10 text-[11px]">
                      {c.name} <span className="font-semibold" style={{ color: PLAN_COLORS[c.plan] || "#71717a" }}>{(c.plan || "").toUpperCase()}</span>
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
          {users.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-500">Aucun utilisateur.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Licenses({ plans, modules, onSaved }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-licenses">
      {plans.map((p) => <PlanCard key={p.key} plan={p} modules={modules} onSaved={onSaved} />)}
    </div>
  );
}

function PlanCard({ plan, modules, onSaved }) {
  const [d, setD] = useState(plan);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setD(plan); }, [plan]);
  const dirty = JSON.stringify(d) !== JSON.stringify(plan);
  const toggleMod = (k) => setD((s) => ({ ...s, modules: s.modules.includes(k) ? s.modules.filter((m) => m !== k) : [...s.modules, k] }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/license-plans/${plan.key}`, { name: d.name, monthly_price: d.monthly_price, yearly_price: d.yearly_price, trial_days: d.trial_days, modules: d.modules });
      toast.success(`Licence ${d.name} enregistrée`); await onSaved();
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };
  const color = PLAN_COLORS[plan.key] || "#71717a";
  return (
    <div className="rounded-2xl border border-white/10 bg-card p-5 flex flex-col" data-testid={`admin-plan-${plan.key}`} style={{ borderTop: `3px solid ${color}` }}>
      <div className="flex items-center gap-2 mb-3"><span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} /><input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="font-cabinet font-bold bg-transparent outline-none flex-1 min-w-0" /></div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Num label="€/mois" v={d.monthly_price} on={(v) => setD({ ...d, monthly_price: v })} tid={`price-m-${plan.key}`} />
        <Num label="€/an" v={d.yearly_price} on={(v) => setD({ ...d, yearly_price: v })} tid={`price-y-${plan.key}`} />
        <Num label="Essai (j)" v={d.trial_days} on={(v) => setD({ ...d, trial_days: v })} tid={`trial-${plan.key}`} />
      </div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Modules inclus</p>
      <div className="space-y-1 flex-1 max-h-72 overflow-y-auto pr-1">
        {modules.map((m) => {
          const on = d.modules.includes(m.key);
          return (
            <button key={m.key} data-testid={`plan-${plan.key}-mod-${m.key}`} onClick={() => toggleMod(m.key)}
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-sm ${on ? "bg-klein/10 text-foreground" : "text-zinc-500 hover:bg-white/[0.04]"}`}>
              <span className={`w-4 h-4 rounded flex items-center justify-center border ${on ? "border-transparent" : "border-white/20"}`} style={on ? { background: color } : {}}>{on && <Check size={12} className="text-white" />}</span>
              {m.label}
            </button>
          );
        })}
      </div>
      <button data-testid={`plan-save-${plan.key}`} onClick={save} disabled={!dirty || saving} className="mt-4 h-9 rounded-lg bg-klein text-zinc-950 font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-1.5">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer</button>
    </div>
  );
}

function Num({ label, v, on, tid }) {
  return (
    <div>
      <label className="block text-[10px] text-zinc-500 mb-1">{label}</label>
      <input type="number" min="0" data-testid={tid} value={v} onChange={(e) => on(parseInt(e.target.value || "0", 10))} className="w-full bg-background border border-white/10 rounded-md px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-klein/20" />
    </div>
  );
}
